import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyPasteurMode({ brand, today, Copy }: ModeRendererProps) {
  // Pasteur gradient: medical blue to containment green
  const pasteurGradient = (
    <defs>
      <linearGradient id="pasteurGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1976D2" />
        <stop offset="50%" stopColor="#388E3C" />
        <stop offset="100%" stopColor="#FFA726" />
      </linearGradient>
      <radialGradient id="epiGlow" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#FFA726" stopOpacity={0.5} />
        <stop offset="100%" stopColor="#1976D2" stopOpacity={0} />
      </radialGradient>
    </defs>
  );

  // Transmission networks: epidemic spreading
  const transmissionNetworks: JSX.Element[] = [];
  const nodeCount = 32;
  const nodes: Array<{x: number, y: number, infected: boolean}> = [];
  
  for (let i = 0; i < nodeCount; i++) {
    const angle = (i * Math.PI * 2) / nodeCount;
    const radius = 180 + (i % 3) * 40;
    const x = 397 + radius * Math.cos(angle);
    const y = 561.5 + radius * Math.sin(angle);
    const infected = Math.random() > 0.6;
    nodes.push({ x, y, infected });
  }

  // Draw connections
  nodes.forEach((node1, i) => {
    nodes.forEach((node2, j) => {
      if (i < j) {
        const dx = node2.x - node1.x;
        const dy = node2.y - node1.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 150 && (node1.infected || node2.infected)) {
          transmissionNetworks.push(
            <line
              key={`trans-${i}-${j}`}
              x1={node1.x}
              y1={node1.y}
              x2={node2.x}
              y2={node2.y}
              stroke={node1.infected && node2.infected ? "#FFA726" : "#388E3C"}
              strokeWidth={1}
              opacity={0.3}
            />
          );
        }
      }
    });
  });

  // Draw nodes
  const networkNodes = nodes.map((node, i) => (
    <circle
      key={`node-${i}`}
      cx={node.x}
      cy={node.y}
      r={node.infected ? 8 : 5}
      fill={node.infected ? "#FFA726" : "#388E3C"}
      opacity={0.7}
    />
  ));

  // R₀ surfaces: reproduction number visualization
  const r0Surfaces: JSX.Element[] = [];
  for (let i = 0; i < 12; i++) {
    const radius = 80 + i * 22;
    const r0 = 1 + i * 0.2;
    const color = r0 < 1.5 ? "#388E3C" : r0 < 2.5 ? "#FFA726" : "#F44336";
    r0Surfaces.push(
      <circle
        key={`r0-${i}`}
        cx={397}
        cy={561.5}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={2}
        opacity={0.3}
      />
    );
  }

  // Ring buffers: containment zones
  const ringBuffers: JSX.Element[] = [];
  const bufferRadii = [120, 180, 240];
  bufferRadii.forEach((radius, i) => {
    ringBuffers.push(
      <circle
        key={`buffer-${i}`}
        cx={397}
        cy={561.5}
        r={radius}
        fill="none"
        stroke="#1976D2"
        strokeWidth={3}
        opacity={0.4}
        strokeDasharray="12,8"
      />
    );
  });

  // Epidemic waves
  const epidemicWaves: JSX.Element[] = [];
  for (let i = 0; i < 14; i++) {
    const y = 120 + i * 65;
    const points = [];
    for (let j = 0; j < 13; j++) {
      const x = 70 + j * 55;
      const wave = Math.exp(-Math.pow((j - 6) / 3, 2)) * 40 * Math.sin(i * 0.5);
      points.push(`${x},${y + wave}`);
    }
    epidemicWaves.push(
      <polyline
        key={`wave-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke="url(#pasteurGrad)"
        strokeWidth={1.5}
        opacity={0.3}
      />
    );
  }

  // Central containment core
  const containmentCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={90} fill="url(#epiGlow)" />
      <circle cx={397} cy={561.5} r={65} fill="none" stroke="#388E3C" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={45} fill="none" stroke="#1976D2" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={25} fill="none" stroke="#FFA726" strokeWidth={2} />
      {/* Medical cross */}
      <line x1={397} y1={536.5} x2={397} y2={586.5} stroke="#FFFFFF" strokeWidth={4} />
      <line x1={372} y1={561.5} x2={422} y2={561.5} stroke="#FFFFFF" strokeWidth={4} />
    </g>
  );

  return (
    <g>
      {pasteurGradient}
      {epidemicWaves}
      {r0Surfaces}
      {ringBuffers}
      {transmissionNetworks}
      {networkNodes}
      {containmentCore}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">{Copy?.title}</text>
        <text x={0} y={22} fontSize={14}>{Copy?.subtitle}</text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>{Copy?.date}</text>
      </g>
    </g>
  );
}
